// Product card behaviour.
//
// The hover image swap is pure CSS - two stacked photographs and an opacity
// transition. What needs script is the size rail, which adds a variant to the
// cart straight from the grid, and quick view, which opens the product without
// leaving the page.
//
// ponytail: no framework, no store. Two custom elements and a shared dialog.

// Horizon's cart icon listens for this, so dispatching it keeps the header
// count correct without reaching into its module graph.
const CART_LINES_UPDATE = 'shopify:cart:lines-update';

const cartAddUrl = () => globalThis.Theme?.routes?.cart_add_url ?? '/cart/add';

async function addVariant(variantId) {
  const response = await fetch(cartAddUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
  });
  if (!response.ok) throw new Error(`add to cart failed: ${response.status}`);
  const body = await response.json();
  document.dispatchEvent(new CustomEvent(CART_LINES_UPDATE, { bubbles: true, detail: body }));
  return body;
}

class AmajiProductCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onActivate);
    this.addEventListener('keydown', this.#onKeydown);
  }

  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const chip = event.target.closest?.('.amaji-card__size');
    if (!chip) return;
    event.preventDefault();
    this.#addSize(chip);
  };

  #onActivate = (event) => {
    const swatch = event.target.closest?.('.amaji-card__swatch');
    if (swatch) {
      this.#showSwatch(swatch);
      return;
    }
    const chip = event.target.closest?.('.amaji-card__size');
    if (!chip) return;
    event.preventDefault();
    this.#addSize(chip);
  };

  // A colour swatch swaps the card's main photo to that variant's own image.
  #showSwatch(swatch) {
    const img = this.querySelector('.amaji-card__image--main');
    if (img && swatch.dataset.src) {
      if (swatch.dataset.srcset) img.srcset = swatch.dataset.srcset;
      img.src = swatch.dataset.src;
    }
    this.querySelectorAll('.amaji-card__swatch').forEach((el) => el.classList.toggle('is-selected', el === swatch));
  }

  async #addSize(chip) {
    const variantId = chip.dataset.variantId;
    // Without a variant id there is nothing to add - send the shopper to the
    // product rather than failing silently.
    if (!variantId) {
      globalThis.location.assign(this.dataset.productUrl);
      return;
    }
    if (chip.getAttribute('aria-busy') === 'true') return;
    chip.setAttribute('aria-busy', 'true');
    try {
      await addVariant(variantId);
      chip.classList.add('amaji-card__size--added');
    } catch (error) {
      console.error(error);
      globalThis.location.assign(this.dataset.productUrl);
    } finally {
      chip.removeAttribute('aria-busy');
    }
  }
}

/** The shared drawer, created once and reused by every card on the page. */
let quickViewDialog = null;

function quickViewPanel() {
  if (quickViewDialog) return quickViewDialog;
  quickViewDialog = document.createElement('dialog');
  quickViewDialog.className = 'amaji-quick-view';
  quickViewDialog.innerHTML = '<div class="amaji-quick-view__body-host"></div>';
  // A click on the backdrop closes it, as it would on any drawer.
  quickViewDialog.addEventListener('click', (event) => {
    if (event.target === quickViewDialog) quickViewDialog.close();
    if (event.target.closest('.amaji-quick-view__close')) quickViewDialog.close();
  });
  document.body.append(quickViewDialog);
  return quickViewDialog;
}

class AmajiQuickView extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#open);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.#open(event);
      }
    });
  }

  #open = async (event) => {
    const url = this.dataset.productUrl;
    if (!url) return;
    event.preventDefault();
    this.setAttribute('aria-busy', 'true');
    try {
      // The alternate template renders the panel and nothing else, so this
      // fetches a fraction of a product page rather than the whole thing.
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}view=quick-view`);
      if (!response.ok) throw new Error(`quick view failed: ${response.status}`);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const panel = parsed.querySelector('.amaji-quick-view__panel');
      if (!panel) throw new Error('no quick view panel in the response');
      const dialog = quickViewPanel();
      // Adopt rather than move: the parsed document is inert, and its custom
      // elements only upgrade once they belong to this document.
      dialog.querySelector('.amaji-quick-view__body-host').replaceChildren(
        document.adoptNode(panel)
      );
      dialog.showModal();
    } catch (error) {
      // A quick view that cannot load is not a dead end; the product page is.
      console.error(error);
      globalThis.location.assign(url);
    } finally {
      this.removeAttribute('aria-busy');
    }
  };
}


if (!customElements.get('amaji-product-card')) {
  customElements.define('amaji-product-card', AmajiProductCard);
}
if (!customElements.get('amaji-quick-view')) {
  customElements.define('amaji-quick-view', AmajiQuickView);
}
